import {
  createApi,
  fetchBaseQuery,
  BaseQueryApi,
} from "@reduxjs/toolkit/query/react";
// import { RootState } from "../../types/rtk/api-config/types";
import { config as appConfig } from "../../config";
import { RootState } from "../../types/rtk/api-config/types";
import { setAuth } from "../auth/auth";
import { exchangeDelegationToken } from "../auth/delegateExchange";

/**
 * apiv5 path: rewrite a tracker-scoped path to its tracker-scoped form by
 * injecting the tracker id. Non-tracker paths are returned untouched.
 *   "/tracker/" | "/tracker"        -> "/tracker/{id}"
 *   "/tracker/subscriptions"        -> "/tracker/{id}/subscriptions"
 *   "/tracker/retention/offers"     -> "/tracker/{id}/retention/offers"
 *   "/user/..." | "/payment/..."    -> unchanged
 */
export const scopeTrackerUrl = (url: string, trackerId: number): string => {
  if (url === "/tracker" || url === "/tracker/") return `/tracker/${trackerId}`;
  if (url.startsWith("/tracker/"))
    return `/tracker/${trackerId}/${url.slice("/tracker/".length)}`;
  return url;
};

const query = fetchBaseQuery({
  baseUrl: appConfig.REACT_APP_APIV4_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const auth = (getState() as RootState).auth;

    if (auth.token) {
      // apiv5 access token
      headers.set("authorization", `Bearer ${auth.token}`);
    } else if (auth.external_id) {
      // legacy external_id (raw)
      headers.set("authorization", `${auth.external_id}`);
    }

    return headers;
  },
});

/**
 * Every route goes out authenticated when we hold a credential, `/public/*`
 * included. `/public/offers` is the only `/public` route this app calls and it
 * is personalised server-side: the payment API derives the displayed currency
 * from the caller's `user.country` (and hides options they already own). Sent
 * anonymously it falls back to the global EUR price list, so a Swiss or British
 * customer picked an offer in EUR and only discovered the CHF/GBP market price
 * at checkout.
 */
export const baseQuery = async (
  args: any,
  api: BaseQueryApi,
  extraOptions: {}
) => {
  const auth = (api.getState() as RootState).auth;

  // apiv5: the access token is short-lived (no refresh). When we hold a durable
  // delegation token but have no access token yet, exchange it first so the
  // request goes out authenticated.
  if (auth.delegation_token && !auth.token) {
    try {
      const { access_token } = await exchangeDelegationToken(auth.delegation_token);
      api.dispatch(setAuth({ token: access_token }));
    } catch {
      // fall through; the request will 401 and surface the error
    }
  }

  const reauth = (api.getState() as RootState).auth;

  // apiv5: inject tracker_id into tracker-scoped paths. Legacy path is untouched.
  // Guard against a non-integer tracker_id (e.g. a NaN persisted from a bad launch
  // URL) — Number.isInteger rejects null/undefined/NaN, so "/tracker/NaN" never ships.
  const trackerId = reauth.tracker_id;
  if (reauth.token && trackerId !== undefined && Number.isInteger(trackerId)) {
    if (typeof args === "string") {
      args = scopeTrackerUrl(args, trackerId);
    } else if (args && typeof args.url === "string") {
      args = { ...args, url: scopeTrackerUrl(args.url, trackerId) };
    }
  }

  let result = await query(args, api, extraOptions);

  // apiv5: the access token likely expired (5 min, no refresh). Re-exchange the
  // durable delegation token once and retry, so navigation survives expiry.
  if (result.error && result.error.status === 401 && reauth.delegation_token) {
    try {
      const { access_token } = await exchangeDelegationToken(reauth.delegation_token);
      api.dispatch(setAuth({ token: access_token }));
      result = await query(args, api, extraOptions);
    } catch {
      // re-exchange failed (delegation token exhausted/expired); keep the 401
    }
  }

  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQuery,
  endpoints: (builder) => ({}),
  tagTypes: ['SubscriptionOptions'],
});
